import { useEffect, useRef, useState } from "react";
import { Card, Col, Row, Form, Button } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { 
  getEmailEventConfigs, 
  addEmailEventConfig, 
  updateEmailEventConfig,
  getEmailTemplates
} from "../../../services/MailerService.js";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from '../common/Toast/toast';
import { useAuth } from "../../../context/AuthContext";
import AsyncSelectDropdown from "../common/CustomAsyncDropdown.jsx";
import "../common/SettingsStepForm.css";

const WarnIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v5M12 16.5v.01" />
  </svg>
);

// Renders as a page on /add-email-event-config and as the body of a
// dialog when ListEmailEventConfigs opens it. With none of these props it
// reads the URL and navigates on finish, exactly as the route always did.
function AddEmailEventConfig({
  // Shown inside a dialog rather than as a page: the component drops its
  // own back-link and page chrome, since the dialog supplies them.
  embedded = false,
  // The record to edit. Falls back to the :id route param.
  recordId,
  // Called after a successful save instead of navigating away.
  onDone,
  // Called when the form is dismissed instead of navigating away.
  onCancel,
}) {
  const { id: routeId } = useParams();
  const id = recordId ?? routeId;
  const [configData, setConfigData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loading, setLoading] = useState(false);
  // What the template search box holds. On the component rather than in
  // the field so it survives a re-render of the Formik tree.
  const [tplQuery, setTplQuery] = useState("");
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  // Leaving the form means "go back to the list" on the route, and "close
  // the dialog" when embedded. Every exit goes through these two so the
  // component never has to know which it is.
  const leave = () => {
    if (onCancel) onCancel();
    else navigate("/email-event-configs");
  };

  const finish = () => {
    if (onDone) onDone();
    else navigate("/email-event-configs");
  };

  // Predefined event key options
  const eventKeyOptions = [
    { value: "reset-password-request", label: "Reset Password Request" },
    { value: "new-student", label: "New Student Welcome" },
    { value: "course-enrollment", label: "Course Enrollment" },
    { value: "payment-confirmation", label: "Payment Confirmation" },
    { value: "batch-completion", label: "Batch Completion" },
    { value: "certificate-issued", label: "Certificate Issued" },
    { value: "attendance-reminder", label: "Attendance Reminder" },
    { value: "assignment-due", label: "Assignment Due" },
    { value: "quiz-available", label: "Quiz Available" },
    { value: "course-feedback", label: "Course Feedback Request" },
    { value: "account-activation", label: "Account Activation" },
    { value: "password-changed", label: "Password Changed Confirmation" },
    { value: "login-alert", label: "Login Alert Notification" },
    { value: "course-reminder", label: "Course Start Reminder" },
    { value: "exam-notification", label: "Exam Notification" },
  ];

  useEffect(() => {
    // Check permissions for both add and edit
    const permission = id ? "email-event-config" : "email-event-config";
    const action = id ? "update" : "add";
    
    if (!hasPermission(permission, action, "lms")) {
      if (!hasShownToast.current) {
        toast.error(`You do not have permission to ${id ? 'edit' : 'add'} email event configurations.`);
        leave();
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate, id]);

  useEffect(() => {
    loadEmailTemplates();
  }, []);

  useEffect(() => {
    if (id) {
      fetchConfigDetails(id);
    } else {
      setConfigData({
        eventKey: "",
        emailtemplateIds: "",
        isDeleted: false
      });
      setIsEditing(false);
    }
  }, [id]);

  const loadEmailTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await getEmailTemplates(5000);
      
      // Handle API response structure
      let templateData = [];
      if (response && response.data && Array.isArray(response.data)) {
        templateData = response.data;
      } else if (response && Array.isArray(response)) {
        templateData = response;
      }
      
      setEmailTemplates(templateData);
    } catch (error) {
      console.error("Error loading email templates:", error);
      toast.error("Error loading email templates!");
      setEmailTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchConfigDetails = async (configId) => {
    try {
      setLoading(true);
      const response = await getEmailEventConfigs(5000);
      
      // Handle API response structure
      let configs = [];
      if (response && response.data && Array.isArray(response.data)) {
        configs = response.data;
      } else if (response && Array.isArray(response)) {
        configs = response;
      }
      
      const selectedConfig = configs.find(
        (config) => config.id?.toString() === configId
      );
      
      if (selectedConfig) {
        setConfigData(selectedConfig);
        setIsEditing(true);
      } else {
        toast.error("Email event configuration not found!");
        leave();
      }
    } catch (error) {
      console.error("Error fetching config details:", error);
      toast.error("Error loading email event configuration!");
      leave();
    } finally {
      setLoading(false);
    }
  };

  const getTemplateOptions = () => {
    return emailTemplates.map((template) => ({
      value: template.id,
      label: template.name,
    }));
  };

  const getSelectedTemplate = (templateId) => {
    return emailTemplates.find(template => template.id === templateId);
  };

  const validationSchema = Yup.object().shape({
    eventKey: Yup.string()
      .min(3, "Event key must be at least 3 characters")
      .required("Event Key is required"),
    emailtemplateIds: Yup.string().required("Email Template is required"),
  });

  const getInitialValues = () => {
    if (configData) {
      return {
        eventKey: configData.eventKey || "",
        emailtemplateIds: configData.emailtemplateIds || "",
        isDeleted: configData.isDeleted || false
      };
    }
    
    return {
      eventKey: "",
      emailtemplateIds: "",
      isDeleted: false
    };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      
      // Prepare payload
      const payload = {
        eventKey: values.eventKey.trim(),
        emailtemplateIds: values.emailtemplateIds,
        isDeleted: false
      };

      // console.log("Submission Payload:", payload);
      // console.log("Is Editing:", isEditing);
      // console.log("Config ID:", id);

      if (isEditing) {
        await updateEmailEventConfig(payload, id);
        toast.success("Email event configuration updated successfully!");
      } else {
        await addEmailEventConfig(payload);
        toast.success("Email event configuration created successfully!");
      }
      
      finish();
    } catch (error) {
      console.error("Submission error:", error);
      
      // Handle different error types
      let errorMessage = `Failed to ${isEditing ? "update" : "create"} email event configuration.`;
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid data provided. Please check all fields.";
      } else if (error.response?.status === 401) {
        errorMessage = "You are not authorized to perform this action.";
      } else if (error.response?.status === 409) {
        errorMessage = "A configuration with this event key already exists.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading state
  if (loading || (!configData && id)) {
    // The page's spinner is a card inside a container-fluid, which in a
    // dialog is a card inside a card. The dialog gets the bare one.
    if (embedded) {
      return (
        <div className="sf">
          <div className="sf-empty">Loading configuration…</div>
        </div>
      );
    }

    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading email event configuration...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── The dialog build ───────────────────────────────────────────────
     One step, not two: there are only two answers to give, and splitting
     two fields across two screens is ceremony rather than clarity. The
     field language is the `sf-*` one the stepped settings dialogs use,
     so this dialog and those read as the same family.
     ------------------------------------------------------------------ */
  if (embedded) {
    const templates = getTemplateOptions();

    return (
      <div className="sf">
        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, setFieldValue, setFieldTouched, submitForm, isSubmitting }) => {
            const eventBad = touched.eventKey && errors.eventKey;
            const tplBad = touched.emailtemplateIds && errors.emailtemplateIds;

            const shown = tplQuery.trim()
              ? templates.filter((t) =>
                  String(t.label || "")
                    .toLowerCase()
                    .includes(tplQuery.trim().toLowerCase())
                )
              : templates;

            const chosenTemplate = templates.find(
              (t) => String(t.value) === String(values.emailtemplateIds)
            );

            return (
              <Form
                noValidate
                onSubmit={(e) => {
                  // Nothing in here is type="submit", so the only thing
                  // that reaches this is Enter pressed inside a field.
                  e.preventDefault();
                  submitForm();
                }}
              >
                <div className="sf-field">
                  <div className="sf-label-row">
                    <label className="sf-label" htmlFor="eec-event">
                      Event<span className="sf-req">*</span>
                    </label>
                  </div>
                  <select
                    id="eec-event"
                    className={`sf-input${eventBad ? " sf-input--bad" : ""}`}
                    value={values.eventKey}
                    onChange={(e) => setFieldValue("eventKey", e.target.value)}
                    onBlur={() => setFieldTouched("eventKey", true, true)}
                  >
                    <option value="">Select an event…</option>
                    {eventKeyOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {eventBad ? (
                    <p className="sf-error"><WarnIcon />{errors.eventKey}</p>
                  ) : (
                    <p className="sf-hint">
                      What has to happen for this mail to be sent.
                    </p>
                  )}
                </div>

                <div className="sf-field">
                  <div className="sf-label-row">
                    <label className="sf-label">
                      Template<span className="sf-req">*</span>
                    </label>
                  </div>

                  {/* One template, not several -- a round box, not a tick
                      box. Searchable because the list is every template in
                      the system, and a plain select gives no way through it
                      but scrolling. */}
                  <div className="sf-pick-bar">
                    <div className="sf-search">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.5-3.5" />
                      </svg>
                      <input
                        type="text"
                        className="sf-search-input"
                        value={tplQuery}
                        placeholder="Search templates…"
                        autoComplete="off"
                        onChange={(e) => setTplQuery(e.target.value)}
                      />
                    </div>
                  </div>

                  <p className="sf-tally">
                    {chosenTemplate ? (
                      <>
                        <b>{chosenTemplate.label}</b> selected
                      </>
                    ) : (
                      <>None selected · {templates.length} available</>
                    )}
                    {tplQuery.trim() && ` · ${shown.length} shown`}
                  </p>

                  {loadingTemplates ? (
                    <div className="sf-empty">Loading templates…</div>
                  ) : shown.length === 0 ? (
                    <div className="sf-empty">
                      {templates.length === 0
                        ? "No email templates exist yet."
                        : `Nothing matches “${tplQuery.trim()}”.`}
                    </div>
                  ) : (
                    <ul className="sf-picklist">
                      {shown.map((t) => {
                        const on = String(t.value) === String(values.emailtemplateIds);
                        return (
                          <li key={t.value}>
                            <button
                              type="button"
                              className={`sf-pick sf-pick--one${on ? " is-on" : ""}`}
                              aria-pressed={on}
                              onClick={() => {
                                setFieldTouched("emailtemplateIds", true, false);
                                // Picking the one already picked clears it,
                                // so a choice made by mistake is undoable
                                // without reloading the form.
                                setFieldValue("emailtemplateIds", on ? "" : t.value);
                              }}
                            >
                              <span className="sf-box" aria-hidden="true" />
                              <span className="sf-pick-name">{t.label}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {tplBad && (
                    <p className="sf-error"><WarnIcon />{errors.emailtemplateIds}</p>
                  )}
                </div>

                <footer className="sf-foot">
                  <button key="cancel" type="button" className="sf-btn" onClick={leave}>
                    Cancel
                  </button>

                  <span className="sf-foot-gap" />

                  {/* Not type="submit": a submit button inherits the
                      browser's default action for a click, which is how a
                      button that changes identity mid-click can save a form
                      it was not meant to. Nothing here is one. */}
                  <button
                    key="save"
                    type="button"
                    className="sf-btn sf-btn--main"
                    disabled={isSubmitting}
                    onClick={() => {
                      setFieldTouched("eventKey", true, true);
                      setFieldTouched("emailtemplateIds", true, true);
                      submitForm();
                    }}
                  >
                    {isSubmitting && <span className="sf-spin" aria-hidden="true" />}
                    {isSubmitting
                      ? isEditing ? "Updating…" : "Saving…"
                      : isEditing ? "Update configuration" : "Create configuration"}
                  </button>
                </footer>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">
                  {isEditing ? "Edit Email Event Configuration" : "Add New Email Event Configuration"}
                </h4>
                <small className="text-muted">
                  {isEditing ? "Update the email event mapping" : "Create a new mapping between system events and email templates"}
                </small>
              </div>
              <Link to="/email-event-configs" className="btn btn-secondary">
                <i className="fa fa-arrow-left me-2"></i>
                Back to List
              </Link>
            </Card.Header>
            
            <Card.Body>
              <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  setFieldValue,
                  isSubmitting,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      {/* Event Configuration Section */}
                      <Col md={12}>
                        <h5 className="mb-3" style={{ color: "#EA580C" }}>
                          <i className="fa fa-cog me-2"></i>
                          Event Configuration
                        </h5>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-dark">Event Key <span className="text-danger">*</span></Form.Label>
                          <AsyncSelectDropdown
                            value={eventKeyOptions.find(option => option.value === values.eventKey) || null}
                            onChange={(selectedOption) => setFieldValue("eventKey", selectedOption?.value || "")}
                            options={eventKeyOptions}
                            placeholder="Select Event Key..."
                            isClearable
                          />
                          {errors.eventKey && touched.eventKey && (
                            <Form.Text className="text-danger">{errors.eventKey}</Form.Text>
                          )}
                          <Form.Text className="text-muted">
                            The system event that will trigger this email template
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-dark">Email Template <span className="text-danger">*</span></Form.Label>
                          <AsyncSelectDropdown
                            value={getTemplateOptions().find(option => option.value === values.emailtemplateIds) || null}
                            onChange={(selectedOption) => setFieldValue("emailtemplateIds", selectedOption?.value || "")}
                            options={getTemplateOptions()}
                            placeholder="Select Email Template..."
                            isClearable
                            isLoading={loadingTemplates}
                            isDisabled={loadingTemplates || getTemplateOptions().length === 0}
                          />
                          {errors.emailtemplateIds && touched.emailtemplateIds && (
                            <Form.Text className="text-danger">{errors.emailtemplateIds}</Form.Text>
                          )}
                          {getTemplateOptions().length === 0 && !loadingTemplates && (
                            <Form.Text className="text-warning">
                              No email templates available. Please create a template first.
                            </Form.Text>
                          )}
                          <Form.Text className="text-muted">
                            The email template to use when this event is triggered
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      {/* Configuration Preview Section */}
                      <Col md={12}>
                        <h5 className="mb-3 mt-4" style={{ color: "#EA580C" }}>
                          <i className="fa fa-info-circle me-2"></i>
                          Configuration Preview
                        </h5>
                      </Col>

                      <Col md={12}>
                        <div className="alert alert-info">
                          <h6><i className="fa fa-eye me-2"></i>Configuration Summary</h6>
                          <Row>
                            <Col md={6}>
                              <p className="mb-2">
                                <strong>Event:</strong> {values.eventKey ? 
                                  eventKeyOptions.find(opt => opt.value === values.eventKey)?.label : 
                                  <span className="text-muted">Not selected</span>
                                }
                              </p>
                              <p className="mb-2">
                                <strong>Event Key:</strong> {values.eventKey || 
                                  <span className="text-muted">Not selected</span>
                                }
                              </p>
                            </Col>
                            <Col md={6}>
                              <p className="mb-2">
                                <strong>Template:</strong> {values.emailtemplateIds ? 
                                  getTemplateOptions().find(opt => opt.value === values.emailtemplateIds)?.label : 
                                  <span className="text-muted">Not selected</span>
                                }
                              </p>
                              <p className="mb-0">
                                <strong>Template ID:</strong> {values.emailtemplateIds || 
                                  <span className="text-muted">Not selected</span>
                                }
                              </p>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      {/* Template Details Section */}
                      {values.emailtemplateIds && (
                        <Col md={12}>
                          <h5 className="mb-3" style={{ color: "#EA580C" }}>
                            <i className="fa fa-envelope me-2"></i>
                            Selected Template Details
                          </h5>
                          {(() => {
                            const selectedTemplate = getSelectedTemplate(values.emailtemplateIds);
                            if (selectedTemplate) {
                              return (
                                <div className="card bg-light">
                                  <div className="card-body">
                                    <Row>
                                      <Col md={6}>
                                        <p className="mb-2"><strong>Template Name:</strong> {selectedTemplate.name}</p>
                                        <p className="mb-2"><strong>Slug:</strong> {selectedTemplate.slug}</p>
                                        <p className="mb-0"><strong>Subject:</strong> {selectedTemplate.subject}</p>
                                      </Col>
                                      <Col md={6}>
                                        <p className="mb-2"><strong>Title:</strong> {selectedTemplate.title}</p>
                                        <p className="mb-0"><strong>Description:</strong> {selectedTemplate.description}</p>
                                      </Col>
                                    </Row>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </Col>
                      )}

                      {/* Help Section */}
                      <Col md={12}>
                        <h5 className="mb-3 mt-4" style={{ color: "#EA580C" }}>
                          <i className="fa fa-question-circle me-2"></i>
                          How It Works
                        </h5>
                      </Col>

                      <Col md={12}>
                        <div className="alert email-helper-alert">
                          <h6><i className="fa fa-lightbulb me-2"></i>Understanding Email Event Configurations</h6>
                          <ul className="mb-0">
                            <li><strong>Event Key:</strong> A unique identifier for system events (e.g., when a user requests password reset)</li>
                            <li><strong>Email Template:</strong> The HTML email template that will be sent when the event occurs</li>
                            <li><strong>Trigger:</strong> When the system event happens, it automatically sends the associated email template</li>
                            <li><strong>Variables:</strong> Templates can use dynamic variables like name, emails, etc.</li>
                          </ul>
                        </div>
                      </Col>
                    </Row>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                      <Link to="/email-event-configs" className="btn btn-outline-secondary">
                        <i className="fa fa-times me-2"></i>
                        Cancel
                      </Link>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting || loadingTemplates || getTemplateOptions().length === 0}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {isEditing ? "Updating..." : "Creating..."}
                          </>
                        ) : (
                          <>
                            <i className={`fa ${isEditing ? 'fa-save' : 'fa-plus'} me-2`}></i>
                            {isEditing ? "Update Configuration" : "Create Configuration"}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default AddEmailEventConfig;
